import { useEffect, useState } from "react"
import styled from "styled-components"
import { Button, TextInput } from "@prosolve/common-components-ui"
import { FaPlus, FaSyncAlt } from "react-icons/fa"
import useDataMongoJobs from "../../dataHooks/useDataMongoJobs"
import useSnackbar from "../../hooks/useSnackbar"
import useMe from "../../hooks/useMe"
import ConfirmDeleteJobModal from "../Jobs/ConfirmDeleteJobModal"
import ConfirmRunJobModal from "../Jobs/ConfirmRunJobModal"
import JobFormModal from "../Jobs/JobFormModal"
import JobHistoryModal from "../Jobs/JobHistoryModal"
import { MutedText } from "../Jobs/JobTable"
import { getJobId, mongoIntervalUnitOptions, toCopiedJob } from "../Jobs/jobUtils"
import MongoJobTable from "./MongoJobTable"

const Container = styled.div`
  padding: 20px;
  color: ${({ theme }) => theme.colors.text};
`

const ColumnGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const RowGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const Header = styled(RowGroup)`
  justify-content: space-between;
  flex-wrap: wrap;
`

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
`

const Group = styled.div`
  background: ${({ theme }) => theme.backgrounds.pure};
  border: ${({ theme }) => theme.borders.main};
  border-radius: ${({ theme }) => theme.borderRadius.main};
  padding: 20px;
`

const PanelTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`

export default () => {
  const [search, setSearch] = useState('')
  const [formJob, setFormJob] = useState()
  const [historyJob, setHistoryJob] = useState()
  const [deleteJob, setDeleteJob] = useState()
  const [jobToRun, setJobToRun] = useState()

  const { newMessage } = useSnackbar()
  const { hasEdit } = useMe()

  const {
    isLoading,
    jobs,
    jobHistory,
    fetchMongoJobs,
    fetchCreateMongoJob,
    fetchUpdateMongoJob,
    fetchToggleMongoJob,
    fetchDeleteMongoJob,
    fetchRunMongoJob,
    fetchMongoJobHistory
  } = useDataMongoJobs()

  useEffect(() => {
    fetchMongoJobs()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshHistory = async (job = historyJob) => {
    const jobId = getJobId(job)
    if (jobId) {
      await fetchMongoJobHistory(jobId)
    }
  }

  const openHistory = async (job) => {
    setHistoryJob(job)
    await refreshHistory(job)
  }

  const openClone = (job) => {
    setFormJob(toCopiedJob(job, jobs))
  }

  const submitJob = async (payload) => {
    const jobId = getJobId(formJob)
    const savedJob = jobId
      ? await fetchUpdateMongoJob(jobId, payload)
      : await fetchCreateMongoJob(payload)

    if (savedJob) {
      newMessage({ message: jobId ? 'Mongo job updated' : 'Mongo job created', severity: 'success' })
      await fetchMongoJobs()
    }

    return savedJob
  }

  const toggleJob = async (job) => {
    const updatedJob = await fetchToggleMongoJob(getJobId(job), !job.enabled)

    if (updatedJob) {
      newMessage({ message: updatedJob.enabled ? 'Mongo job enabled' : 'Mongo job disabled', severity: 'success' })
      await fetchMongoJobs()
    }
  }

  const confirmRunJob = async () => {
    const job = jobToRun
    const result = await fetchRunMongoJob(getJobId(job))

    if (result) {
      newMessage({
        message: result.status === 'success' ? 'Mongo job ran successfully' : 'Mongo job run failed',
        severity: result.status === 'success' ? 'success' : 'failure'
      })
      setJobToRun()
      if (getJobId(historyJob) === getJobId(job)) {
        await refreshHistory(job)
      }
    }
  }

  const confirmDeleteJob = async () => {
    const result = await fetchDeleteMongoJob(getJobId(deleteJob))

    if (result?.success) {
      newMessage({ message: 'Mongo job deleted', severity: 'success' })
      setDeleteJob()
      if (getJobId(historyJob) === getJobId(deleteJob)) {
        setHistoryJob()
      }
      await fetchMongoJobs()
    }
  }

  return (
    <Container>
      <ColumnGroup style={{ gap: '15px' }}>
        <Header>
          <ColumnGroup>
            <Title>MongoDB Atlas Job Manager</Title>
            <MutedText>Manage portalJob_ scheduled triggers and wrapper functions.</MutedText>
          </ColumnGroup>
          <RowGroup>
            <Button onClick={fetchMongoJobs}>
              <RowGroup>
                <FaSyncAlt />
                Refresh
              </RowGroup>
            </Button>
            {hasEdit &&
              <Button $colored={true} onClick={() => setFormJob({})}>
                <RowGroup>
                  <FaPlus />
                  New Mongo Job
                </RowGroup>
              </Button>
            }
          </RowGroup>
        </Header>

        <Group>
          <ColumnGroup>
            <RowGroup style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <PanelTitle>Mongo Jobs</PanelTitle>
              <TextInput
                value={search}
                placeholder='Search Mongo jobs'
                width='260px'
                onChange={setSearch}
              />
            </RowGroup>
            <MongoJobTable
              jobs={jobs}
              search={search}
              isLoading={isLoading}
              onRun={setJobToRun}
              onHistory={openHistory}
              onEdit={setFormJob}
              onClone={openClone}
              onToggle={toggleJob}
              onDelete={setDeleteJob}
            />
          </ColumnGroup>
        </Group>
      </ColumnGroup>

      {formJob &&
        <JobFormModal
          title={formJob.isClone ? 'Clone Mongo Job' : (getJobId(formJob) ? 'Edit Mongo Job' : 'New Mongo Job')}
          job={formJob}
          isNameDisabled={Boolean(getJobId(formJob))}
          intervalUnitOptions={mongoIntervalUnitOptions}
          onClose={() => setFormJob()}
          onSubmit={submitJob}
        />
      }

      {historyJob &&
        <JobHistoryModal
          job={historyJob}
          history={jobHistory}
          isLoading={isLoading}
          onRefresh={() => refreshHistory()}
          onClose={() => setHistoryJob()}
        />
      }

      {deleteJob &&
        <ConfirmDeleteJobModal
          job={deleteJob}
          description='This will delete the scheduled trigger and wrapper function from MongoDB Atlas.'
          onClose={() => setDeleteJob()}
          onConfirm={confirmDeleteJob}
        />
      }

      {jobToRun &&
        <ConfirmRunJobModal
          job={jobToRun}
          title='Run Mongo Job'
          description='This will invoke the MongoDB Atlas job immediately outside of its schedule.'
          onClose={() => setJobToRun()}
          onConfirm={confirmRunJob}
        />
      }
    </Container>
  )
}
